import { createApiClient, type AuthTokens } from "@nutrayan/api-client";
import toast from "react-hot-toast";
import { getEnv } from "./env";
import { useAuth } from "./auth";
import { useTenantOverride } from "./tenant-override";

const env = getEnv();

// Same-origin by default: the Vite dev proxy (and the production reverse proxy)
// forwards /api and /admin/api to the backend, so no CORS and no env required.
const baseUrl =
  env.VITE_API_BASE_URL ?? (typeof window !== "undefined" ? window.location.origin : "");

// Guard so a burst of concurrent 401s produces exactly one logout/redirect/toast.
let loggingOut = false;

function onAuthCleared() {
  if (loggingOut) return;
  loggingOut = true;
  useAuth.getState().clear();
  useTenantOverride.getState().setTenant(null);
  toast.error("Your session has expired. Please sign in again.", { id: "admin-session-expired" });
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
    // Hard redirect guarantees a single, clean navigation out of any SPA route.
    window.location.assign("/login");
  }
}

export const apiClient = createApiClient({
  baseUrl,
  tenantCode: env.VITE_DEFAULT_TENANT_CODE,
  defaultLocale: "en-IN",
  getAuthToken: () => useAuth.getState().token ?? undefined,
  // Bootstrap super admins can scope the session to a storefront tenant
  // (BLK-API-015); the backend ignores this header for anyone else.
  getExtraHeaders: () => {
    const tenantId = useTenantOverride.getState().tenantId;
    return tenantId ? { "X-Tenant-Override": tenantId } : undefined;
  },
  getRefreshToken: () => useAuth.getState().refreshToken ?? undefined,
  onAuthRefreshed: (tokens: AuthTokens) =>
    useAuth.getState().setTokens(tokens.accessToken, tokens.refreshToken),
  onAuthCleared,
});
